import { FC, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useTranslations } from 'next-intl'
import useSWR from 'swr'
import clsx from 'clsx'
import InputWithButton from '@/components/common/input-with-button'
import ServiceProgressModal from './service-progress-modal'
import {
  queryServiceProgress,
  ApiResponse,
  OrderProgressData,
  OrderStatusCode,
} from '@/service/fault-reporting.service'

export interface ServiceProgressProps {
  // 组件不需要外部 props
}

const serviceProgressSchema = Yup.object().shape({
  serviceCode: Yup.string()
    .required('请输入服务编号')
    .min(4, '服务编号长度不能少于4位')
    .max(50, '服务编号长度不能超过50位'),
})

// 工单状态码 → i18n key 映射
const ORDER_STATUS_I18N_KEY: Record<OrderStatusCode, string> = {
  '1': 'OrderStatus.PendingConfirmation',
  '2': 'OrderStatus.Processing',
  '3': 'OrderStatus.Escalated',
  '4': 'OrderStatus.Completed',
  '5': 'OrderStatus.Reviewed',
  '6': 'OrderStatus.Cancelled',
}

// 已完成的终态状态码
const COMPLETED_STATUS_CODES: OrderStatusCode[] = ['4', '5']

type ModalType = 'completed' | 'processing' | 'error'

function resolveModalType(statusCode: OrderStatusCode): ModalType {
  if (COMPLETED_STATUS_CODES.includes(statusCode)) return 'completed'
  return 'processing'
}

const ServiceProgress: FC<ServiceProgressProps> = () => {
  const t = useTranslations()
  const [submittedServiceCode, setSubmittedServiceCode] = useState<string>('')
  const [queryError, setQueryError] = useState<string | null>(null)

  const [modalVisible, setModalVisible] = useState(false)
  const [modalType, setModalType] = useState<ModalType>('error')
  const [modalStatusText, setModalStatusText] = useState<string>('')

  type FormData = {
    serviceCode: string
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(serviceProgressSchema) as any,
  })

  const openModal = useCallback((type: ModalType, statusText = '') => {
    setModalType(type)
    setModalStatusText(statusText)
    setModalVisible(true)
  }, [])

  const { isValidating: isQuerying } = useSWR<ApiResponse<OrderProgressData>>(
    submittedServiceCode ? queryServiceProgress.swrkey(submittedServiceCode) : null,
    () => queryServiceProgress(submittedServiceCode),
    {
      onSuccess: (data) => {
        if (data.code !== 200 || !data.data) {
          setQueryError(data.message || t('ServiceProgressNotFound'))
          openModal('error')
          return
        }
        setQueryError(null)
        const status = data.data.order_status
        const statusLabel = t(ORDER_STATUS_I18N_KEY[status] ?? 'OrderStatus.Unknown')
        openModal(resolveModalType(status), statusLabel)
      },
      onError: () => {
        setQueryError(t('FaultReportingForm.NetworkError'))
        openModal('error')
      },
    }
  )

  const onFormSubmit = (data: FormData) => {
    setSubmittedServiceCode(data.serviceCode)
    setQueryError(null)
  }

  return (
    <div className={clsx('w-full p-6 laptop:p-0')}>
      {/* 使用一套结构，通过响应式类名区分 PC 和 H5 */}
      <div
        className={clsx(
          'w-full',
          'flex flex-col laptop:flex-row',
          'gap-6 laptop:gap-0',
          'laptop:justify-between',
          'items-start'
        )}
      >
        {/* 标题 - PC 版本设置 sticky 效果 */}
        <h2
          className={clsx(
            'w-full laptop:w-75',
            'text-24 laptop:text-36',
            'font-semibold',
            'text-black/90',
            'flex-shrink-0',
            'laptop:sticky laptop:top-30'
          )}
        >
          {t('ServiceProgress')}
        </h2>

        {/* 内容区域 */}
        <div className={clsx('w-full', 'laptop:w-200', 'laptop:flex-shrink-0')}>
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            className={clsx(
              'w-full',
              'bg-white/90 laptop:backdrop-blur-[48px]',
              'rounded-lg laptop:rounded-lg',
              'p-0',
              'flex flex-col',
              'gap-6 laptop:gap-4',
              'laptop:min-h-[110px]'
            )}
          >
            <p
              className={clsx(
                'w-full',
                'text-14 laptop:text-14',
                'font-semibold',
                'text-black/60',
                'leading-[22px]'
              )}
            >
              {t('ServiceProgressDesc')}
            </p>

            <InputWithButton
              inputProps={register('serviceCode')}
              placeholder={t('EnterServiceCode')}
              inputType="text"
              inputDisabled={isQuerying}
              buttonText={t('FaultReportingForm.Confirm')}
              buttonDisabled={isQuerying}
              isLoading={isQuerying}
              loadingText={t('Loading')}
              inputWidthPC="w-[299px]"
              buttonWidthPC="w-[102px]"
              buttonWidthH5="w-[100px]"
              error={errors.serviceCode?.message || queryError || null}
            />
          </form>
        </div>
      </div>

      <ServiceProgressModal
        visible={modalVisible}
        type={modalType}
        statusText={modalStatusText}
        onClose={() => setModalVisible(false)}
      />
    </div>
  )
}

export default ServiceProgress
